import { useState } from "react";

/**
 *
 * ESTRUCTURA DEL ESTADO:
 * selected = {
 *   [userId]: {
 *     [productId]: cantidad
 *   }
 * }
 * 
 * Ejemplo:
 * {
 *   "abc123": { "p1": 2, "p2": 5 },
 *   "def456": { "p3": 1 }
 * }
 * 
 * - Cada userId representa un usuario único.
 * - Cada productId representa un producto asignado a ese usuario.
 * - La cantidad es un número entero >= 0.
 */

export default function useSelectedProducts(initialState = {}) {
  const [selected, setSelected] = useState(initialState);

  /**
   * Agrega un nuevo usuario con un objeto vacío de productos.
   * No hace nada si el usuario ya existe.
   */
  const addUser = (userId, defaultProducts) => {
    setSelected((prev) => {
      if (prev[userId]) return prev;
      return {
        ...prev,
        [userId]: defaultProducts,
      };
    });
  };

  /**
   * Elimina por completo un usuario y sus productos asignados.
   */
  const removeUser = (userId) => {
    setSelected((prev) => {
      if (!prev[userId]) return prev;
      const { [userId]: _, ...rest } = prev;
      return rest;
    });
  };

  /**
   * Actualiza la cantidad de un producto para un usuario dado.
   * Si qty < 0 o el usuario no existe, no se actualiza nada.
   */
  const updateProductQty = (userId, productId, qty) => {
    setSelected((prev) => {
      if (!prev[userId] || qty < 0) return prev;
      return {
        ...prev,
        [userId]: {
          ...prev[userId],
          [productId]: qty,
        },
      };
    });
  };

  /**
   * Elimina un producto específico de un usuario.
   * Si el producto no existe, no hace nada.
   */
  const removeProduct = (userId, productId) => {
    setSelected((prev) => {
      if (!prev[userId] || !(productId in prev[userId])) return prev;
      const { [productId]: _, ...restProducts } = prev[userId];
      return {
        ...prev,
        [userId]: restProducts,
      };
    });
  };

  /**
   * Agrega un producto con cantidad inicial (por defecto 1) a un usuario existente.
   * Si el usuario no existe o la cantidad no es válida, no se hace nada.
   */
  const addProduct = (userId, productId, qty = 1) => {
    setSelected((prev) => {
      if (!prev[userId] || qty <= 0) return prev;
      return {
        ...prev,
        [userId]: {
          ...prev[userId],
          [productId]: qty,
        },
      };
    });
  };

  return {
    selected,        // Estado actual
    setSelected,     // Acceso directo si se necesita
    addUser,
    removeUser,
    addProduct,
    updateProductQty,
    removeProduct,
  };
}
